import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next'; 
import { 
  Save, ArrowLeft, Paperclip, History, ShieldCheck, Search,  
  ChevronLeft, ChevronRight, Settings, Layers, Trash2, 
  Workflow, Plus, Minus, AlertTriangle, FileText, Link
} from 'lucide-react';

import UniversalLookup from './UniversalLookup';
import StatusModal from './StatusModal';
import StatusHistoryModal from './StatusHistoryModal';
import AttachmentsModal from './AttachmentsModal';
import AuditHistoryModal from './AuditHistoryModal';
import SmartPrintButton from './PrintButton';
import WorkflowActionModal from './WorkflowActionModal';
import WorkflowHistoryModal from './WorkflowHistoryModal';
import { useFontSize } from './FontSizeContext';
import { usePermissions } from './usePermissions';

const ContractDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const isNew = !id || id === 'new';
    
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'ar' || i18n.language === 'ur';

    const { fontSize, changeFontSize } = useFontSize();
    const { canCreate, canUpdate, canDelete } = usePermissions('contracts');
    const [activeTab, setActiveTab] = useState('General');
    const [loading, setLoading] = useState(false);
    
    // Modals Control
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [isDocModalOpen, setIsDocModalOpen] = useState(false);
    const [isWFHistoryOpen, setIsWFHistoryOpen] = useState(false);
    const [showAudit, setShowAudit] = useState(false);
    const [attCount, setAttCount] = useState(0);

    const [nav, setNav] = useState({ next: null, prev: null, current_pos: 0, total: 0 });
    const [wfContext, setWfContext] = useState({ can_start: false, can_action: false });

    const session = JSON.parse(localStorage.getItem('user') || '{}');
    const [data, setData] = useState({
        contract_num: '', description: '', contract_type: 'SUPPLY', status: 'DRAFT',
        vendor_id: '', contact_person: '', vendor_email: '', start_date: '', end_date: '',
        total_amount: 0, currency: 'EGP', org_id: session.org_id, site_id: session.site_id,
        slanum: '', // 🎯 الربط مع الـ SLA
        sla: { response_time_hrs: 24, resolution_time_hrs: 48, uptime_percentage: 99.9, penalty_per_hour: 0 },
        terms: [], // 🎯 تحويل الشروط لأسطر (Array of objects)
        lines: []
    });

    const [lookup, setLookup] = useState({ isOpen: false, title: '', type: '', field: '', filters: {}, targetIdx: null });
    const isReadOnly = ['APPROVED', 'ACTIVE', 'EXPIRED', 'CLOSE'].includes(data.status);

    const loadContract = useCallback(async () => {
        if (isNew) return;
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`/api/contracts/${id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            // حماية المصفوفات من الـ Undefined
            const safeData = {
                ...res.data,
                lines: Array.isArray(res.data.lines) ? res.data.lines : [],
                terms: Array.isArray(res.data.terms) ? res.data.terms : [] // 🎯 حماية مصفوفة الشروط
            };
            setData(safeData);
            
            const attRes = await axios.get(`/api/attachments/count/CONTRACTS/${id}`, { headers: { 'Authorization': `Bearer ${token}` } });
            setAttCount(attRes.data.count);

            const wfRes = await axios.get(`/api/workflow/check-runtime/CONTRACTS/${id}`, { headers: { 'Authorization': `Bearer ${token}` } });
            setWfContext(wfRes.data);

            const navRes = await axios.get(`/api/contracts/${id}/navigation${location.search}`, { headers: { 'Authorization': `Bearer ${token}` } });
            setNav(navRes.data);
        } catch (err) { toast.error(t('common.load_fail')); }
        finally { setLoading(false); }
    }, [id, isNew, location.search, t]);

    useEffect(() => { loadContract(); }, [loadContract]);

    useEffect(() => {
        const openLog = () => setShowAudit(true);
        window.addEventListener('GLOBAL_LOG_HANDLER', openLog);
        return () => window.removeEventListener('GLOBAL_LOG_HANDLER', openLog);
    }, []);

    const handleSave = async () => {
        if (!data.description) return toast.error(t('contract.messages.descMandatory'));
        const tid = toast.loading(t('common.saving'));
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                toast.error(t('common.sessionExpired'));
                navigate('/login');
                return;
            }
            const res = await axios.post('/api/contracts/save', data, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            toast.success(t('common.save_success'), { id: tid });
            if (isNew) navigate(`/contracts/${res.data.contract_num}${location.search}`);
            else loadContract();
        } catch (err) { toast.error(t('common.save_fail'), { id: tid }); }
    };

    const handleDelete = async () => {
        const result = await Swal.fire({
            title: t('contract.swal.deleteTitle'), text: t('contract.swal.deleteText'),
            icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33', cancelButtonColor: '#1a3353',
            confirmButtonText: t('common.yes'), cancelButtonText: t('common.cancel')
        });
        if (result.isConfirmed) {
            try {
                const token = localStorage.getItem('token');
                await axios.delete(`/api/contracts/${data.contract_id}`, { headers: { 'Authorization': `Bearer ${token}` }});
                toast.success(t('common.delete_success'));
                navigate('/contracts');
            } catch (err) { toast.error(t('common.delete_fail')); }
        }
    };

    useEffect(() => {
        const saveH = () => handleSave();
        const statusH = () => setShowStatusModal(true);
        window.addEventListener('GLOBAL_SAVE_HANDLER', saveH);
        window.addEventListener('GLOBAL_STATUS_HANDLER', statusH);
        return () => { 
            window.removeEventListener('GLOBAL_SAVE_HANDLER', saveH); 
            window.removeEventListener('GLOBAL_STATUS_HANDLER', statusH); 
        };
    }, [data]);

    useEffect(() => {
        const triggerDelete = () => handleDelete();
        window.addEventListener('GLOBAL_DELETE_HANDLER', triggerDelete);
        return () => window.removeEventListener('GLOBAL_DELETE_HANDLER', triggerDelete);
    }, [handleDelete]);

    const handleSearch = (field, title, type, idx = null) => {
        setLookup({ isOpen: true, title, type, field, filters: { org_id: data.org_id }, targetIdx: idx });
    };

    const handleLookupSelect = (row) => {
        const val = row.id || row.value || row.comp_num || row.slanum;
        
        if (lookup.field === 'vendor_id') {
            setData(prev => ({
                ...prev,
                vendor_id: row.comp_num || val,      
                contact_person: row.contact_name || '',    
                vendor_email: row.email || '',            
                currency: row.currency || prev.currency    
            }));
            toast.success(t('contract.messages.vendorSelected', { name: row.name || val }));
        } 
        else if (lookup.field === 'slanum') {
            setData(prev => ({ ...prev, slanum: row.slanum || val })); // 🎯 ربط الـ SLA
        }
        else {
            setData(prev => ({ ...prev, [lookup.field]: val }));
        }
        setLookup(p => ({...p, isOpen: false}));
    };

    const goTo = (target) => target && navigate(`/contracts/${target}${location.search}`);

    return (
        <div className={`flex flex-col h-full bg-surface-app font-bold uppercase tracking-tighter ${isRTL ? 'font-cairo dir-rtl text-right' : 'font-sans text-left'}`} style={{ fontSize: `${fontSize}px` }}>
            <UniversalLookup {...lookup} onClose={() => setLookup(p => ({...p, isOpen: false}))} onSelect={handleLookupSelect} />
            
            {showStatusModal && <StatusModal currentStatus={data.status} recordId={data.contract_num} appName="CONTRACTS" onClose={() => { setShowStatusModal(false); loadContract(); }} />}
            {showHistoryModal && <StatusHistoryModal isOpen={showHistoryModal} appName="CONTRACTS" recordId={data.contract_num} onClose={() => setShowHistoryModal(false)} />}
            <AttachmentsModal isOpen={isDocModalOpen} ownertable="CONTRACTS" ownerid={data.contract_num} onClose={() => {setIsDocModalOpen(false); loadContract();}} />
            {isWFHistoryOpen && <WorkflowHistoryModal module="CONTRACTS" refId={id} onClose={() => setIsWFHistoryOpen(false)} />}
            <AuditHistoryModal isOpen={showAudit} onClose={() => setShowAudit(false)} tableName="contracts" recordId={data.contract_num} />

            {/* --- TOOLBAR --- */}
            <div className={`bg-primary-dark text-white p-1.5 flex justify-between items-center shadow-lg px-4 shrink-0 `}>
                <div className={`flex items-center gap-3 `}>
                    <button onClick={() => navigate(`/contracts${location.search}`)} className="p-1.5 hover:bg-white/10 rounded-full transition-all active:scale-90"><ArrowLeft size={18} className={isRTL ? 'rotate-180' : ''}/></button>
                    {!isNew && (
                        <div className={`flex items-center bg-black/30 border border-white/10 rounded px-1 py-0.5 gap-1 font-mono text-[10px] `}>
                            <button disabled={!nav.prev} onClick={() => goTo(nav.prev)} className="p-1 hover:text-status-warning disabled:opacity-20"><ChevronLeft size={16} className={isRTL ? 'rotate-180' : ''}/></button>
                            <span className="px-3 text-status-warning tracking-widest">{nav.current_pos} / {nav.total}</span>
                            <button disabled={!nav.next} onClick={() => goTo(nav.next)} className="p-1 hover:text-status-warning disabled:opacity-20"><ChevronRight size={16} className={isRTL ? 'rotate-180' : ''}/></button>
                        </div>
                    )}
                </div>

                <div className={`flex gap-2 items-center `}>
                    {((isNew && canCreate) || (!isNew && canUpdate)) && (
                    <button onClick={handleSave} disabled={isReadOnly || loading} className="bg-status-success px-6 py-2 rounded-sm flex items-center gap-2 hover:bg-status-success/90 font-black shadow-md transition-all active:scale-95 disabled:opacity-30">
                        <Save size={14}/> {loading ? t('common.saving') : t('contract.actions.save')}
                    </button>
                    )}

                    <WorkflowActionModal moduleName="CONTRACTS" refId={id} currentStatus={data.status} onActionComplete={loadContract} />
                    {!isNew && <SmartPrintButton moduleName="CONTRACTS" refId={data.contract_num} />}

                    <div className="h-6 w-px bg-white/20 mx-2"></div>
                    <button onClick={() => setIsDocModalOpen(true)} className="relative p-2 text-sky hover:text-white transition-colors" title={t('common.attachments')}>
                        <Paperclip size={18}/>
                        {attCount > 0 && <span className="absolute top-1 right-0 bg-status-danger text-[8px] w-4 h-4 rounded-full flex items-center justify-center border border-white font-black">{attCount}</span>}
                    </button>
                    <button onClick={() => setIsWFHistoryOpen(true)} className="p-2 text-status-warning" title={t('contract.actions.wfHistory')}><Workflow size={18}/></button>
                    <button onClick={() => setShowHistoryModal(true)} className="p-2 text-sky" title={t('common.history')}><History size={18}/></button>
                    {canDelete && !isNew && <button onClick={handleDelete} className="p-2 text-status-danger hover:bg-status-danger/20 rounded" title={t('common.delete')}><Trash2 size={18}/></button>}

                    <div className={`flex items-center gap-2 bg-white/10 px-3 py-1 rounded-full border border-white/10 shadow-inner ${isRTL ? 'mr-4 flex-row-reverse' : 'ml-4'}`}>
                        <button onClick={() => changeFontSize(-1)} className="hover:text-status-warning"><Minus size={14} /></button>
                        <span className="font-black text-[10px] text-sky">{fontSize}</span>
                        <button onClick={() => changeFontSize(1)} className="hover:text-status-warning"><Plus size={14} /></button>
                    </div>
                </div>
            </div>

            {/* --- HEADER --- */}
            <div className={`bg-surface-card border-b px-6 py-4 flex items-center gap-12 shadow-sm shrink-0 z-10 `}>
                <HeaderField label={t('contract.fields.contractNum')} value={data.contract_num} isBold width="w-36" disabled={!isNew} onChange={v => setData({...data, contract_num: v})} isRTL={isRTL}/>
                <HeaderField label={t('assets.headers.desc')} value={data.description} onChange={v => setData({...data, description: v})} width="w-[500px]" disabled={isReadOnly} isRTL={isRTL} placeholder={t('contract.placeholders.desc')} />
                <HeaderField label={t('assets.headers.status')} value={data.status} disabled width="w-28" isStatus onClick={() => setShowStatusModal(true)} isRTL={isRTL}/>
                <HeaderField label={t('assets.headers.site')} value={data.site_id} disabled width="w-24" isRTL={isRTL}/>
            </div>

            {/* --- TABS --- */}
            <div className={`flex bg-surface-card border-b px-4 shrink-0 shadow-sm z-10 `}>
                {['General', 'SLA (Performance)', 'Terms & Legal', 'Contract Lines'].map(tKey => (
                    <button
                        key={tKey}
                        onClick={() => setActiveTab(tKey)}
                        className={`px-10 py-3.5 transition-all text-[10px] border-b-4 font-black ${activeTab === tKey ? 'border-primary-dark text-primary-dark bg-surface-panel shadow-inner' : 'border-transparent text-ink-secondary hover:text-ink'}`}
                    >
                        {t(`contract.tabs.${tKey.replace(/[^a-zA-Z]/g, '').toLowerCase()}`)}
                    </button>
                ))}
            </div>

            {/* --- CONTENT AREA --- */}
            <div className="flex-1 overflow-y-auto p-8 bg-surface-panel custom-scrollbar">
                <div className="max-w-7xl mx-auto animate-in fade-in slide-in-from-top-4 duration-500 pb-20">
                    
                    {activeTab === 'General' && (
                        <div className="grid grid-cols-12 gap-10">
                            <div className="col-span-8 space-y-8">
                                <SectionCard title={t('contract.sections.identity')} isRTL={isRTL}>
                                    <div className={`grid grid-cols-2 gap-x-20 gap-y-8 ${isRTL ? 'dir-rtl' : ''}`}>
                                        <div className={`flex flex-col border-b py-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                                            <label className="text-[9px] text-ink-secondary italic mb-1 uppercase font-black">{t('common.type')}</label>
                                            <select className={`bg-transparent outline-none font-black text-primary text-[14px] uppercase ${isRTL ? 'dir-rtl' : ''}`} value={data.contract_type} onChange={e => setData({...data, contract_type: e.target.value})} disabled={isReadOnly}>
                                                <option value="SUPPLY">{t('contract.options.supply')}</option>
                                                <option value="LABOR">{t('contract.options.labor')}</option>
                                                <option value="LEASE">{t('contract.options.lease')}</option>
                                            </select>
                                        </div>
                                        <DataField label={t('contract.fields.vendorRef')} value={data.vendor_id} onSearch={() => handleSearch('vendor_id', t('common.search'), 'companies')} disabled={isReadOnly} isRTL={isRTL} />
                                        <DataField label={t('contract.fields.startDate')} value={data.start_date} type="date" onChange={v => setData({...data, start_date: v})} disabled={isReadOnly} isRTL={isRTL} />
                                        <DataField label={t('contract.fields.endDate')} value={data.end_date} type="date" onChange={v => setData({...data, end_date: v})} disabled={isReadOnly} isRTL={isRTL} />
                                        <DataField label={t('contract.fields.contractValue')} value={data.total_amount} type="number" onChange={v => setData({...data, total_amount: v})} disabled={isReadOnly} isRTL={isRTL} />
                                        <DataField label={t('contract.fields.currency')} value={data.currency} onChange={v => setData({...data, currency: v})} disabled={isReadOnly} isRTL={isRTL} />
                                    </div>
                                </SectionCard>
                            </div>
                            <div className="col-span-4 space-y-8">
                                <SectionCard title={t('contract.sections.communication')} isRTL={isRTL}>
                                    <div className="space-y-6">
                                        <StaticField label={t('contract.fields.contactPerson')} value={data.contact_person} icon={<Settings size={14}/>} isRTL={isRTL}/>
                                        <StaticField label={t('contract.fields.vendorEmail')} value={data.vendor_email} icon={<ShieldCheck size={14}/>} isRTL={isRTL}/>
                                    </div>
                                </SectionCard>
                                <div className="bg-primary-dark p-6 rounded shadow-2xl text-white">
                                    <div className={`flex items-center gap-2 mb-4 border-b border-white/10 pb-2 `}>
                                        <AlertTriangle size={18} className="text-status-warning"/>
                                        <span className="font-black text-[10px] tracking-widest uppercase">{t('contract.sections.auditInfo')}</span>
                                    </div>
                                    <div className="space-y-3">
                                        <div className={`flex justify-between `}><span className="text-white/50 text-[9px] italic uppercase">{t('appSettings.org')}</span><span className="font-black">{data.org_id}</span></div>
                                        <div className={`flex justify-between `}><span className="text-white/50 text-[9px] italic uppercase">{t('assets.headers.site')}</span><span className="font-black">{data.site_id}</span></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'SLA (Performance)' && (
                        <div className="max-w-5xl mx-auto space-y-8 animate-in slide-in-from-bottom-4 duration-500">
                            {/* 🎯 بطاقة ربط الـ SLA */}
                            <SectionCard title={t('contract.sections.masterSLA')} isRTL={isRTL}>
                                <div className={`grid grid-cols-2 gap-10 ${isRTL ? 'dir-rtl' : ''}`}>
                                    <DataField 
                                        label={t('contract.fields.slaRef')} 
                                        value={data.slanum} 
                                        onSearch={() => handleSearch('slanum', t('common.search'), 'sla')} 
                                        disabled={isReadOnly} 
                                        isRTL={isRTL} 
                                    />
                                    <div className={`flex items-center gap-4 bg-primary-soft p-4 border border-primary/20 rounded-sm `}>
                                        <Link size={24} className="text-primary shrink-0" />
                                        <p className={`text-[10px] text-primary italic font-bold leading-tight ${isRTL ? 'text-right' : 'text-left'}`}>
                                            {t('contract.messages.slaHint')}
                                        </p>
                                    </div>
                                </div>
                            </SectionCard>

                            {/* مؤشرات الأداء الحالية */}
                            <SectionCard title={t('contract.sections.slaBenchmarks')} isRTL={isRTL}>
                                <div className={`grid grid-cols-2 gap-x-20 gap-y-10 ${isRTL ? 'dir-rtl' : ''}`}>
                                    <DataField label={t('contract.fields.respTime')} value={data.sla?.response_time_hrs} type="number" onChange={v => setData({...data, sla: {...data.sla, response_time_hrs: v}})} disabled={isReadOnly} isRTL={isRTL} />
                                    <DataField label={t('contract.fields.resTime')} value={data.sla?.resolution_time_hrs} type="number" onChange={v => setData({...data, sla: {...data.sla, resolution_time_hrs: v}})} disabled={isReadOnly} isRTL={isRTL} />
                                    <DataField label={t('contract.fields.uptime')} value={data.sla?.uptime_percentage} type="number" onChange={v => setData({...data, sla: {...data.sla, uptime_percentage: v}})} disabled={isReadOnly} isRTL={isRTL} />
                                    <DataField label={t('contract.fields.penalty')} value={data.sla?.penalty_per_hour} type="number" onChange={v => setData({...data, sla: {...data.sla, penalty_per_hour: v}})} disabled={isReadOnly} isRTL={isRTL} />
                                </div>
                            </SectionCard>
                        </div>
                    )}

                    {/* 🎯 TAB 3: Terms & Legal (تم تحويلها لأسطر) */}
                    {activeTab === 'Terms & Legal' && (
                        <div className="bg-surface-card border-2 border-line rounded shadow-2xl overflow-hidden animate-in zoom-in-95 duration-500">
                            <div className={`p-4 bg-surface-panel border-b flex justify-between items-center `}>
                                <h3 className={`font-black text-primary-dark text-[12px] flex items-center gap-3 tracking-[0.2em] uppercase italic `}>
                                    <FileText size={18}/> {t('contract.sections.termsList')}
                                </h3>
                                <button
                                    disabled={isReadOnly}
                                    onClick={() => setData(prev => ({...prev, terms: [...(prev.terms||[]), { clause_type: 'GENERAL', description: '' }]}))}
                                    className={`bg-primary text-white px-8 py-2 rounded-sm font-black text-[11px] flex items-center gap-2 hover:bg-black transition-all shadow-md active:scale-95 uppercase `}
                                >
                                    <Plus size={16}/> {t('contract.actions.addTerm')}
                                </button>
                            </div>
                            <table className={`w-full ${isRTL ? 'text-right' : 'text-left'}`}>
                                <thead className="bg-surface-panel border-b text-ink-secondary text-[10px] font-black uppercase tracking-widest">
                                    <tr className={isRTL ? 'flex-row-reverse' : ''}>
                                        <th className={`p-4 border-x w-12 text-center ${isRTL ? 'border-l' : 'border-r'}`}>#</th>
                                        <th className={`p-4 border-x w-48 text-center ${isRTL ? 'border-l' : 'border-r'}`}>{t('contract.tables.clauseType')}</th>
                                        <th className={`p-4 border-x ${isRTL ? 'border-l' : 'border-r'}`}>{t('contract.tables.clauseDesc')}</th>
                                        <th className="p-4 w-12"></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-line-subtle font-bold">
                                    {(Array.isArray(data.terms) ? data.terms : []).map((term, i) => (
                                        <tr key={i} className={`hover:bg-primary-soft transition-colors `}>
                                            <td className={`p-4 border-x text-center text-ink-secondary font-mono italic text-[10px] ${isRTL ? 'border-l' : 'border-r'}`}>{i+1}</td>
                                            <td className={`p-4 border-x text-center ${isRTL ? 'border-l' : 'border-r'}`}>
                                                <select
                                                    className={`w-full bg-transparent outline-none font-black text-status-warning uppercase cursor-pointer ${isRTL ? 'dir-rtl text-right' : 'text-center'}`}
                                                    value={term.clause_type}
                                                    onChange={e => { const nl = [...data.terms]; nl[i].clause_type = e.target.value; setData({...data, terms: nl}); }}
                                                    disabled={isReadOnly}
                                                >
                                                    <option value="GENERAL">{t('contract.options.general')}</option>
                                                    <option value="PAYMENT">{t('contract.options.payment')}</option>
                                                    <option value="TERMINATION">{t('contract.options.termination')}</option>
                                                    <option value="WARRANTY">{t('contract.options.warranty')}</option>
                                                    <option value="LIABILITY">{t('contract.options.liability')}</option>
                                                </select>
                                            </td>
                                            <td className={`p-4 border-x ${isRTL ? 'border-l' : 'border-r'}`}>
                                                <textarea
                                                    className={`w-full bg-transparent outline-none font-black text-primary resize-y min-h-[40px] ${isRTL ? 'text-right' : 'text-left'}`}
                                                    value={term.description}
                                                    placeholder={t('contract.placeholders.termDesc')}
                                                    onChange={e => { const nl = [...data.terms]; nl[i].description = e.target.value; setData({...data, terms: nl}); }}
                                                    disabled={isReadOnly}
                                                />
                                            </td>
                                            <td className="p-4 text-center">
                                                <button disabled={isReadOnly} onClick={() => { const nl = data.terms.filter((_, idx) => idx !== i); setData({...data, terms: nl}); }}>
                                                    <Trash2 size={16} className={`${isReadOnly ? 'text-ink-secondary/40' : 'text-ink-secondary hover:text-status-danger'} transition-colors`}/>
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    {(!Array.isArray(data.terms) || data.terms.length === 0) && (
                                        <tr><td colSpan="4" className="p-20 text-center italic text-ink-secondary font-black uppercase tracking-widest text-[11px]">{t('contract.messages.noTerms')}</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {activeTab === 'Contract Lines' && (
                        <div className="bg-surface-card border-2 border-line rounded shadow-2xl overflow-hidden animate-in zoom-in-95 duration-500">
                            <div className={`p-4 bg-surface-panel border-b flex justify-between items-center `}>
                                <h3 className={`font-black text-primary-dark text-[12px] flex items-center gap-3 tracking-[0.2em] uppercase italic `}><Layers size={18}/> {t('contract.sections.lines')}</h3>
                                <button disabled={isReadOnly} onClick={() => setData(prev => ({...prev, lines: [...(prev.lines||[]), {description: '', qty: 1, unit_price: 0, line_total: 0}]}))} className={`bg-primary text-white px-8 py-2 rounded-sm font-black text-[11px] flex items-center gap-2 hover:bg-black transition-all shadow-md active:scale-95 uppercase `}>
                                    <Plus size={16}/> {t('contract.actions.addLine')}
                                </button>
                            </div>
                            <table className={`w-full ${isRTL ? 'text-right' : 'text-left'}`}>
                                <thead className="bg-surface-panel border-b text-ink-secondary text-[10px] font-black uppercase tracking-widest">
                                    <tr className={isRTL ? 'flex-row-reverse' : ''}>
                                        <th className={`p-4 border-x w-12 text-center ${isRTL ? 'border-l' : 'border-r'}`}>#</th>
                                        <th className={`p-4 border-x ${isRTL ? 'border-l' : 'border-r'}`}>{t('contract.tables.lineDesc')}</th>
                                        <th className={`p-4 border-x text-center w-32 ${isRTL ? 'border-l' : 'border-r'}`}>{t('common.qty')}</th>
                                        <th className={`p-4 border-x w-44 ${isRTL ? 'text-left border-l' : 'text-right border-r'}`}>{t('contract.tables.rate')}</th>
                                        <th className={`p-4 w-48 bg-primary-soft/20 text-primary ${isRTL ? 'text-left' : 'text-right'}`}>{t('contract.tables.lineTotal')}</th>
                                        <th className="p-4 w-12"></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-line-subtle font-bold">
                                    {(Array.isArray(data.lines) ? data.lines : []).map((l, i) => (
                                        <tr key={i} className={`hover:bg-primary-soft transition-colors `}>
                                            <td className={`p-4 border-x text-center text-ink-secondary font-mono italic text-[10px] ${isRTL ? 'border-l' : 'border-r'}`}>{i+1}</td>
                                            <td className={`p-4 border-x ${isRTL ? 'border-l' : 'border-r'}`}><input className={`w-full bg-transparent outline-none font-black text-primary ${isRTL ? 'text-right' : 'text-left'}`} value={l.description} onChange={e => { const nl = [...data.lines]; nl[i].description = e.target.value; setData({...data, lines: nl}); }} disabled={isReadOnly} /></td>
                                            <td className={`p-4 border-x text-center ${isRTL ? 'border-l' : 'border-r'}`}><input type="number" className="w-full text-center bg-transparent outline-none font-black text-primary" value={l.qty} onChange={e => { const nl = [...data.lines]; nl[i].qty = e.target.value; nl[i].line_total = e.target.value * nl[i].unit_price; setData({...data, lines: nl}); }} disabled={isReadOnly} /></td>
                                            <td className={`p-4 border-x ${isRTL ? 'text-left border-l' : 'text-right border-r'}`}><input type="number" className={`w-full bg-transparent outline-none font-black text-primary ${isRTL ? 'text-left' : 'text-right'}`} value={l.unit_price} onChange={e => { const nl = [...data.lines]; nl[i].unit_price = e.target.value; nl[i].line_total = nl[i].qty * e.target.value; setData({...data, lines: nl}); }} disabled={isReadOnly} /></td>
                                            <td className={`p-4 font-black text-status-success bg-status-success-bg/5 text-[14px] italic ${isRTL ? 'text-left' : 'text-right'}`}>{(l.qty * l.unit_price).toLocaleString()}</td>
                                            <td className="p-4 text-center">
                                                <button disabled={isReadOnly} onClick={() => { const nl = data.lines.filter((_, idx) => idx !== i); setData({...data, lines: nl}); }}><Trash2 size={16} className={`${isReadOnly ? 'text-ink-secondary/40' : 'text-ink-secondary hover:text-status-danger'} transition-colors`}/></button>
                                            </td>
                                        </tr>
                                    ))}
                                    {(!Array.isArray(data.lines) || data.lines.length === 0) && (
                                        <tr><td colSpan="6" className="p-20 text-center italic text-ink-secondary font-black uppercase tracking-widest text-[11px]">{t('contract.messages.noLines')}</td></tr>
                                    )}
                                </tbody>
                            </table>
                            <div className={`p-6 bg-surface-panel border-t flex ${isRTL ? 'justify-start' : 'justify-end'}`}>
                                <div className={`flex items-center gap-10 `}>
                                    <span className="text-ink-secondary italic text-[10px] font-black uppercase tracking-widest">{t('contract.fields.grandTotal')}</span>
                                    <span className={`text-3xl font-black text-primary-dark tracking-tighter italic border-b-4 border-status-warning flex items-center gap-2 `}>
                                        {(Array.isArray(data.lines) ? data.lines : []).reduce((acc, curr) => acc + (curr.qty * curr.unit_price), 0).toLocaleString()} 
                                        <span className="text-sm opacity-50 font-sans">{data.currency}</span>
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* --- STATUS BAR --- */}
            <div className={`bg-primary-dark text-white p-2 px-10 flex justify-between items-center shadow-inner shrink-0 z-50 `}>
                <div className={`flex items-center gap-10 text-[10px] font-black uppercase tracking-widest `}>
                    <div className={`flex items-center gap-2 `}><ShieldCheck size={14} className="text-status-warning"/><span>{t('common.site')}: <span className="text-status-warning">{data.site_id}</span></span></div>
                    <div className={`flex items-center gap-2 border-white/10 px-10 ${isRTL ? 'border-r flex-row-reverse' : 'border-l'}`}><FileText size={14} className="text-sky"/><span>{t('common.type')}: <span className="text-sky">{data.contract_type}</span></span></div>
                </div>
                <div className={`flex items-center gap-5 `}>
                    <div className="font-mono text-[9px] text-white/50 italic uppercase">Sync: Online | {t('common.user')}: {session.username}</div>
                    <div className="w-2.5 h-2.5 bg-status-success rounded-full animate-pulse shadow-green-400 shadow-md"></div>
                </div>
            </div>
        </div>
    );
};

// --- المساعدات المرئية الموحدة ---
const HeaderField = ({ label, value, onChange, width, isBold, disabled, isStatus, onClick, placeholder, isRTL }) => (
    <div className={`flex flex-col px-5 border-line shrink-0 ${isRTL ? 'border-l last:border-0 text-right' : 'border-r last:border-0 text-left'}`}>
        <label className="text-[0.9em] text-ink-secondary font-black uppercase tracking-widest mb-0.5 italic leading-none">{label}</label>
        <div className={`flex items-center gap-2 ${isStatus ? 'cursor-pointer hover:bg-surface-panel p-1 rounded-sm transition-all' : ''} `} onClick={onClick}>
            {isStatus && <span className="w-2.5 h-2.5 rounded-full bg-status-success animate-pulse shadow-md"></span>}
            <input
                className={`${width} border-b border-transparent outline-none py-0.5 font-black uppercase ${isBold ? 'text-primary text-[13px] tracking-tighter' : 'text-primary text-[12px]'} bg-transparent transition-all focus:border-primary placeholder:opacity-30 ${isRTL ? 'text-right' : 'text-left'}`}
                value={value || ''} disabled={disabled} onChange={e => onChange && onChange(e.target.value)} placeholder={placeholder}
            />
        </div>
    </div>
);

const SectionCard = ({ title, children, isRTL }) => (
    <div className="bg-surface-card border-2 border-line rounded shadow-xl overflow-hidden transition-all hover:shadow-2xl duration-300">
        <div className={`p-2.5 bg-surface-panel border-b-2 border-line-subtle font-black text-ink-secondary uppercase text-[1.0em] tracking-widest flex items-center gap-3 italic `}>
            <div className="w-1.5 h-5 bg-status-warning rounded-full shadow-lg"></div> {title}
        </div>
        <div className="p-8">{children}</div>
    </div>
);

const DataField = ({ label, value, onChange, onSearch, type = "text", disabled, isRTL }) => (
    <div className={`flex flex-col gap-1 border-b-2 border-line-subtle group focus-within:border-primary-dark transition-all py-1.5 px-1 ${isRTL ? 'text-right' : 'text-left'}`}>
        <label className="text-ink-secondary font-black text-[0.9em] uppercase tracking-widest italic leading-none mb-1 group-hover:text-primary transition-colors">{label}</label>
        <div className={`flex items-center justify-between pb-0.5 h-6 `}>
            <input
                type={type}
                className={`outline-none font-black text-primary bg-transparent w-full text-[13px] tracking-tighter uppercase disabled:opacity-50 ${isRTL ? 'text-right' : 'text-left'}`}
                value={value ? (type === 'date' ? value.split('T')[0] : value) : ''}
                disabled={disabled} onChange={e => onChange && onChange(e.target.value)}
            />
            {onSearch && !disabled && <Search size={16} className="text-primary-hover cursor-pointer hover:text-primary transition-all hover:scale-125 shrink-0" onClick={onSearch} />}
        </div>
    </div>
);

const StaticField = ({ label, value, icon, isRTL }) => (
    <div className={`flex flex-col border-b border-line py-2.5 px-3 bg-surface-card/40 shadow-inner rounded-sm mb-2 border-primary-dark/10 ${isRTL ? 'border-r-4 text-right' : 'border-l-4 text-left'}`}>
        <div className={`flex items-center gap-2 mb-1.5 `}>
            {icon && <span className="text-primary-hover shrink-0">{icon}</span>}
            <label className="text-[0.9em] font-black text-ink-secondary uppercase tracking-widest italic">{label}</label>
        </div>
        <div className="font-black uppercase tracking-[0.1em] text-[13px] text-primary italic">{value || 'NOT_DEFINED'}</div>
    </div>
);

export default ContractDetails;